import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { ModuleFlagKey, SubscriptionFeaturesService } from '../../../services/subscription-features.service';
import { inactiveModuleSlug } from '../../../pages-utils/module-inactive/module-inactive';

/**
 * Type des modules disponibles dans l'abonnement.
 * Alias de `ModuleFlagKey` (source unique dans SubscriptionFeaturesService).
 */
export type ModuleKey = ModuleFlagKey;

/**
 * Guard qui vérifie si un module spécifique est activé dans l'abonnement de l'organisation.
 *
 * Utilisation dans les routes :
 * ```typescript
 * {
 *   path: 'calendar',
 *   canActivate: [isModuleEnabledGuard],
 *   data: { moduleKey: 'calendars_enable' }
 * }
 * ```
 */
export const isModuleEnabledGuard = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const subscriptionFeatures = inject(SubscriptionFeaturesService);

  // Remonte la chaîne parent pour trouver le moduleKey (cas canActivateChild :
  // la route enfant n'a pas de moduleKey propre, il est sur la route parente).
  let moduleKey: ModuleKey | undefined = route.data['moduleKey'];
  if (!moduleKey) {
    let parent = route.parent;
    while (parent) {
      if (parent.data['moduleKey']) {
        moduleKey = parent.data['moduleKey'] as ModuleKey;
        break;
      }
      parent = parent.parent;
    }
  }

  if (!moduleKey) {
    return true;
  }

  const organisation = authService.profile?.organisation;
  if (!organisation) {
    const queryParams = state.url && state.url !== '/' ? { returnUrl: state.url } : undefined;
    return router.createUrlTree(['/sign-in'], queryParams ? { queryParams } : undefined);
  }

  // Source unique de vérité : SubscriptionFeaturesService (abonnement courant
  // = le plus récent actif/trialing valide, fallback FALSE si aucun).
  // Le cas « aucun abonnement courant » est normalement déjà intercepté par
  // SubscriptionActiveGuard, exécuté AVANT ce guard sur toutes les routes module.
  const isEnabled = subscriptionFeatures.moduleEnabled(moduleKey);

  // Module désactivé : une page l'explique (et dit qui peut l'activer) plutôt qu'une redirection
  // muette vers un autre module (MOKA-402).
  if (!isEnabled) {
    return router.createUrlTree(['/module-inactif', inactiveModuleSlug(moduleKey)]);
  }

  return true;
};
